import { useEffect, useState } from "react";
import { ScrollText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError, formatDate } from "@/lib/api";
import { Loading } from "@/components/common";
import { Cell, DataTable, EmptyState, PageHeader, Row, SearchField, Tag } from "./ui";

const ACTION_LABELS = {
  "auth.login": "Login interno",
  "auth.login_google": "Login Google",
  "auth.login_failed": "Tentativa de login falhou",
  "auth.logout": "Logout",
  "session.created": "Sessão criada",
  "session.revoked": "Sessões revogadas",
  "patient.created": "Paciente criado",
  "episode.created": "Episódio criado",
  "support_person.created": "Pessoa da rede de apoio cadastrada",
  "access_grant.created": "Vínculo criado",
  "access_grant.revoked": "Vínculo revogado",
  "invite.created": "Convite gerado",
  "support.activated": "Conta da rede de apoio ativada",
  "terms.accepted": "Termos aceitos",
  "bulletin.created": "Boletim criado",
  "bulletin.published": "Boletim publicado",
  "bulletin.corrected": "Boletim corrigido",
  "bulletin.archived": "Boletim arquivado",
  "medication.created": "Medicamento cadastrado",
  "medication_event.created": "Alteração de medicamentos registrada",
  "bulletin.read": "Boletim lido",
  "medication_event.read": "Alerta de medicamentos lido",
};

export default function PortalAudit() {
  const { api } = useAuth();
  const [logs, setLogs] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/audit", { params: { limit: 150 } });
        setLogs(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
        setLogs([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const term = query.trim().toLowerCase();
  const filtered = (logs || []).filter((log) =>
    !term
      ? true
      : `${ACTION_LABELS[log.action] || ""} ${log.action} ${log.actor_name || ""} ${log.entity_type || ""}`
          .toLowerCase()
          .includes(term)
  );

  return (
    <div data-testid="portal-audit-page" className="space-y-7">
      <PageHeader
        kicker="Registro append-only"
        title="Auditoria"
        subtitle="Eventos nunca são alterados nem apagados. Cada linha guarda ator, papel, entidade e metadados."
      />

      {logs === null ? (
        <Loading label="Carregando auditoria…" />
      ) : logs.length === 0 ? (
        <EmptyState
          testId="audit-empty-state"
          icon={ScrollText}
          title="Nenhum evento registrado ainda"
          description="Assim que a equipe começar a operar o portal, cada ação aparecerá aqui com ator, papel e horário."
        />
      ) : (
        <>
          <SearchField
            testId="audit-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por evento, pessoa ou entidade"
            className="max-w-sm"
          />

          {filtered.length === 0 ? (
            <EmptyState
              testId="audit-empty-filter"
              icon={ScrollText}
              title="Nenhum evento com essa busca"
              description="Limpe o campo de busca para ver os últimos 150 eventos registrados."
            />
          ) : (
            <DataTable
              testId="audit-table"
              columns={[
                { key: "event", label: "Evento" },
                { key: "actor", label: "Ator" },
                { key: "entity", label: "Entidade", className: "hidden md:table-cell" },
                { key: "when", label: "Quando" },
              ]}
            >
              {filtered.map((log) => (
                <Row key={log.id} testId={`audit-row-${log.action}`}>
                  <Cell>
                    <span className="font-semibold text-ink-800">{ACTION_LABELS[log.action] || log.action}</span>
                    <p className="mt-0.5 text-[13px] text-ink-500">{log.action}</p>
                  </Cell>
                  <Cell>
                    <span className="block">{log.actor_name}</span>
                    {log.actor_role ? (
                      <span className="mt-1.5 inline-block">
                        <Tag>{log.actor_role}</Tag>
                      </span>
                    ) : null}
                  </Cell>
                  <Cell className="hidden text-ink-600 md:table-cell">{log.entity_type}</Cell>
                  <Cell className="whitespace-nowrap text-ink-600">{formatDate(log.created_at)}</Cell>
                </Row>
              ))}
            </DataTable>
          )}
        </>
      )}
    </div>
  );
}
